import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Play, Image as ImageIcon, Eye, ThumbsUp, MessageCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { optimizedImage, imageSrcSet, isSaveData } from "@/lib/image-url";
import type { PostMedia } from "@/components/post-card";

export type MediaStripFilter = "all" | "photo" | "video";

type StripPost = {
  id: string;
  title: string;
  post_type: string;
  category: string;
  upvotes: number;
  comment_count: number;
  view_count: number;
  created_at: string;
  latitude: number | null;
  longitude: number | null;
  media: PostMedia[] | null;
};

interface Props {
  title: string;
  icon?: React.ReactNode;
  filter?: MediaStripFilter;
  /** Restrict to a specific community */
  communityId?: string;
  /** Restrict to a specific post type (issue, emergency, project, discussion, …) */
  postType?: string;
  /** Order: trending uses upvotes, views uses view_count, recent uses created_at desc */
  sort?: "trending" | "views" | "recent";
  /** Only fetch posts updated within N days */
  windowDays?: number;
  /** Center for nearby filtering (haversine km) */
  near?: { lat: number; lng: number; radiusKm: number } | null;
  /** Require media to be present (default true). For discussion/emergency strips set false. */
  requireMedia?: boolean;
  limit?: number;
  emptyHint?: string;
}

function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

export function MediaStrip({
  title, icon, filter = "all", communityId, postType, sort = "trending",
  windowDays = 30, near, requireMedia = true, limit = 12, emptyHint,
}: Props) {
  const [posts, setPosts] = useState<StripPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("posts")
        .select("id, title, post_type, category, upvotes, comment_count, view_count, created_at, latitude, longitude, media:post_media(id, url, media_type, position, poster_url, duration_seconds, review_state)")
        .limit(near ? 200 : limit * 3);

      if (communityId) q = q.eq("community_id", communityId);
      if (postType) q = q.eq("post_type", postType as any);
      if (windowDays) {
        const since = new Date(Date.now() - windowDays * 86400_000).toISOString();
        q = q.gte("created_at", since);
      }
      if (near) {
        q = q.not("latitude", "is", null).not("longitude", "is", null);
      }
      if (sort === "trending") q = q.order("upvotes", { ascending: false }).order("created_at", { ascending: false });
      else if (sort === "views") q = q.order("view_count", { ascending: false });
      else q = q.order("created_at", { ascending: false });

      const { data } = await q;
      if (cancel) return;
      let rows = ((data ?? []) as any as StripPost[]);

      // Discovery strips only ever surface video that has cleared review, even
      // for the member who posted it — this is a browse surface, not their draft.
      rows = rows.map((p) => ({
        ...p,
        media: (p.media ?? []).filter((m: any) =>
          m.media_type !== "video" || (m.review_state ?? "approved") === "approved"),
      })) as StripPost[];

      if (requireMedia) {
        rows = rows.filter((p) => {
          const m = p.media ?? [];
          if (!m.length) return false;
          if (filter === "photo") return m.some((x) => x.media_type === "image");
          if (filter === "video") return m.some((x) => x.media_type === "video");
          return true;
        });
      }
      if (near) {
        rows = rows
          .filter((p) => p.latitude != null && p.longitude != null)
          .map((p) => ({ ...p, _d: distanceKm(near, { lat: p.latitude!, lng: p.longitude! }) }) as any)
          .filter((p: any) => p._d <= near.radiusKm)
          .sort((a: any, b: any) => a._d - b._d);
      }
      setPosts(rows.slice(0, limit));
      setLoading(false);
    })();
    return () => { cancel = true; };
  }, [filter, communityId, postType, sort, windowDays, near?.lat, near?.lng, near?.radiusKm, requireMedia, limit]);

  if (loading) {
    return (
      <section className="mb-6">
        <StripHeader title={title} icon={icon} />
        <div className="flex items-center gap-2 px-1 py-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> <span className="text-xs">Loading…</span>
        </div>
      </section>
    );
  }

  if (posts.length === 0) {
    return (
      <section className="mb-6">
        <StripHeader title={title} icon={icon} />
        <p className="px-1 py-3 text-xs text-muted-foreground">{emptyHint ?? "Nothing here yet."}</p>
      </section>
    );
  }

  return (
    <section className="mb-6">
      <StripHeader title={title} icon={icon} />
      <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
        <div className="flex w-max gap-3 pb-2">
          {posts.map((p) => <MediaTile key={p.id} post={p} filter={filter} requireMedia={requireMedia} />)}
        </div>
      </div>
    </section>
  );
}

function StripHeader({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      {icon}
      <h2 className="font-display text-base font-semibold tracking-tight">{title}</h2>
    </div>
  );
}

function MediaTile({ post, filter, requireMedia }: { post: StripPost; filter: MediaStripFilter; requireMedia: boolean }) {
  const media = (post.media ?? []).sort((a, b) => a.position - b.position);
  const preferred = filter === "video"
    ? media.find((m) => m.media_type === "video") ?? media[0]
    : filter === "photo"
      ? media.find((m) => m.media_type === "image") ?? media[0]
      : media[0];
  const isVideo = preferred?.media_type === "video";

  return (
    <Link
      to="/posts/$id"
      params={{ id: post.id }}
      className={cn(
        "group relative block w-44 shrink-0 overflow-hidden rounded-xl border border-border bg-card shadow-[var(--shadow-card)] transition-transform hover:-translate-y-0.5",
        "sm:w-52",
      )}
    >
      <div className="relative aspect-[4/5] bg-muted">
        {preferred ? (
          isVideo ? (
            <>
              {preferred.poster_url ? (
                <img src={preferred.poster_url} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              ) : (
                <div className="h-full w-full bg-black" />
              )}
              <span className="pointer-events-none absolute inset-0 grid place-items-center bg-black/15">
                <span className="grid h-10 w-10 place-items-center rounded-full bg-black/65 text-white"><Play className="h-4 w-4 fill-current" /></span>
              </span>
            </>
          ) : (
            <img src={optimizedImage(preferred.url, 400)} srcSet={imageSrcSet(preferred.url, [300, 400, 600])} sizes="200px" alt="" loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
          )
        ) : (
          <div className="grid h-full w-full place-items-center text-muted-foreground">
            <ImageIcon className="h-6 w-6" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent p-2">
          <p className="line-clamp-2 text-xs font-medium text-white">{post.title}</p>
        </div>
      </div>
      <div className="flex items-center justify-between gap-1 px-2 py-1.5 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1"><Eye className="h-3 w-3" />{post.view_count ?? 0}</span>
        <span className="inline-flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{post.upvotes ?? 0}</span>
        <span className="inline-flex items-center gap-1"><MessageCircle className="h-3 w-3" />{post.comment_count ?? 0}</span>
      </div>
    </Link>
  );
}
